import {
  closeProxy,
  generateProxyKeyframes,
} from '@iconik-io/iconik-api/src/sdk/files';
import {
  httpDeleteAssetsByAssetIdProxiesByProxyId,
  httpPostAssetsByAssetIdProxies,
} from '@iconik-io/iconik-api/src/services/files';
import { unwrapResponseBody } from '@iconik-io/iconik-api/src/utils/unwrap_response_body';

import {
  CloseProxyError,
  CreateProxyError,
  GenerateKeyframesError,
} from '../../errors';
import { UPLOADER_STATUS_PAUSED } from '../../state';
import { unwrapUploadEntityId } from '../../state/upload_entity_id';
import { unwrapProxyUploadKind } from '../../state/upload_kind';
import { unwrapAssetId } from './context';
import type { EntitiesLifecycle } from './entities_lifecycle';
import { deleteDuringTeardown } from './teardown';

const DEFAULT_TRANSCODE_PRIORITY = 5;

/**
 * Uploading a proxy for an asset that already exists.
 *
 * Far smaller than the file lifecycle on purpose: a proxy is a single entity on
 * the asset, so there is no collection to resolve, no duplicate check, and no
 * format, file set or file to create.
 */
export const proxyEntitiesLifecycle: EntitiesLifecycle = {
  async prepareEntities(context) {
    const state = context.getState();
    const uploadKind = unwrapProxyUploadKind(state);
    if (uploadKind.proxyId) {
      return;
    }

    const assetId = unwrapAssetId(context);
    const proxy = await unwrapResponseBody(
      context.apiClient.request(
        httpPostAssetsByAssetIdProxies(assetId, {
          // Everything else in the payload is the consumer's to decide, so it
          // is sent as given.
          ...uploadKind.create,
          asset_id: assetId,
          storage_id: context.storageOptions.id,
          status: 'OPEN',
          content_type: state.fileInfo.mimeType,
        }),
        { abortSignal: context.abortList.newSignal() },
      ),
    ).catch((error) => {
      throw new CreateProxyError(error);
    });

    context.updateState({
      uploadKind: { ...uploadKind, proxyId: proxy.id },
    });
    context.emit('uploadEntityCreated', {
      state: context.getState(),
      entity: proxy,
    });
    context.emit('proxyCreated', { state: context.getState(), proxy });
  },

  /**
   * Keyframes first, then close. Reversing the two leaves the proxy without
   * keyframes, which is the order web arrived at empirically.
   */
  async finalize(context) {
    const state = context.getState();
    const assetId = unwrapAssetId(context);
    const proxyId = unwrapUploadEntityId(state);

    await generateProxyKeyframes({
      client: context.apiClient,
      params: {
        assetId,
        proxyId,
        transcodeOptions: {
          priority:
            state.options.transcodePriority ?? DEFAULT_TRANSCODE_PRIORITY,
          update_proxy_mediainfo: true,
        },
      },
      abortSignal: context.abortList.newSignal(),
    }).catch((error) => {
      throw new GenerateKeyframesError(error);
    });

    await closeProxy({
      client: context.apiClient,
      params: { assetId, proxyId },
      abortSignal: context.abortList.newSignal(),
    }).catch((error) => {
      throw new CloseProxyError(error);
    });
  },

  async cleanupEntities(context) {
    const state = context.getState();
    const uploadKind = unwrapProxyUploadKind(state);
    const proxyId = uploadKind.proxyId;

    if (proxyId) {
      // The asset was there before this upload and stays: only the proxy the
      // upload added to it is removed.
      await deleteDuringTeardown(
        context,
        httpDeleteAssetsByAssetIdProxiesByProxyId(
          unwrapAssetId(context),
          proxyId,
        ),
      );
    }

    context.updateState({
      status: UPLOADER_STATUS_PAUSED,
      chunks: undefined,
      meta: undefined,
      uploadKind: { ...uploadKind, proxyId: undefined },
    });
  },
};
